<template>
  <ScannerButton dense flat :icon="icon" mode="code" :text-color="textColor" @scan="onScan" />
</template>

<script setup lang="ts">
import { Dialog } from 'quasar';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import OpenUrlDialog from 'components/OpenUrlDialog.vue';
import ScannerButton from 'components/scanner/ScannerButton.vue';

withDefaults(defineProps<{ icon?: string; textColor?: string }>(), {
  textColor: 'white',
});

const emit = defineEmits(['navigate', 'search']);

const $router = useRouter();
const store = useStore();

/**
 * @param {Event} event
 * @param {string} result
 */
async function onScan(_, result) {
  const trimmedResult = result.trim();

  /**
   * Handle URL scans
   */
  if (isValidUrl(trimmedResult)) {
    const url = new URL(trimmedResult);
    if (url.hostname.includes('zubie')) {
      $router.push(url.pathname);
      emit('navigate', url.pathname);
    } else {
      Dialog.create({
        component: OpenUrlDialog,
        componentProps: {
          url: url.href,
        },
      }).onOk(() => {
        emit('navigate', url.href);
      });
    }
    return;
  }

  /**
   * Handle possible device serial scans
   */
  if (/^[^\s]+$/.test(trimmedResult)) {
    let possibleSerial = trimmedResult.toUpperCase();
    if (/^[\w\d]+#[0-9A-F]{15}$/.test(possibleSerial)) {
      // Bosch serial pattern, extract the serial
      possibleSerial = possibleSerial.replace(/^.*#/, '');
    }

    const device = await store.dispatch('devices/getDevice', possibleSerial);
    if (!device.error) {
      const url = `/devices/connect/${possibleSerial}`;
      $router.push(url);
      emit('navigate', url);
      return;
    }
  }

  /**
   * Fallback on Universal Search
   */
  emit('search', trimmedResult);
}

function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
</script>
